<template>
  <div style="max-width: 1400px; margin: auto" class="pa-3">
    <!-- Header -->
    <v-toolbar dark flat color="transparent" class="px-0 mb-3">
      <v-toolbar-title>
        <v-icon left>mdi-account-question</v-icon>
        Personalanliegen
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn color="primary" depressed rounded @click="openCreateDialog">
        <v-icon left>mdi-plus</v-icon>
        <span class="d-none d-sm-inline">Neues Anliegen</span>
      </v-btn>
    </v-toolbar>

    <!-- Filter -->
    <v-card dark class="mb-3 pa-3" color="rgba(255,255,255,0.05)" flat>
      <v-row dense>
        <v-col v-if="canManage" cols="12" sm="6" md="4">
          <v-select
            v-model="filterStudio"
            :items="studioFilterOptions"
            item-text="name"
            item-value="_id"
            label="Studio"
            outlined
            dense
            dark
            hide-details
            @change="loadRequests" />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-select v-model="filterStatus" :items="statusFilterOptions" label="Status" outlined dense dark hide-details @change="loadRequests" />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-select
            v-model="filterPriority"
            :items="priorityFilterOptions"
            label="Priorität"
            outlined
            dense
            dark
            hide-details
            @change="loadRequests" />
        </v-col>
      </v-row>
    </v-card>

    <!-- Liste -->
    <v-card dark color="rgba(255,255,255,0.03)" flat>
      <v-data-table
        dark
        :headers="tableHeaders"
        :items="requests"
        :loading="loading"
        :items-per-page="25"
        class="transparent"
        no-data-text="Keine Personalanliegen vorhanden"
        @click:row="openDetailDialog">
        <template v-slot:item.createdAt="{ item }">
          {{ $moment(item.createdAt).format("DD.MM.YYYY") }}
        </template>
        <template v-slot:item.affectedEmployee="{ item }">
          {{ item.affectedEmployee ? item.affectedEmployee.vorname + " " + item.affectedEmployee.nachname : "-" }}
        </template>
        <template v-slot:item.studio="{ item }">
          {{ item.studio ? item.studio.name : "-" }}
        </template>
        <template v-slot:item.priority="{ item }">
          <v-chip x-small :color="priorityColor(item.priority)" dark>
            {{ priorityLabel(item.priority) }}
          </v-chip>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="statusColor(item.status)" dark>
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.signatures="{ item }">
          <v-chip v-if="item.fullySigned" x-small color="green" dark>
            <v-icon left x-small>mdi-check-all</v-icon>
            {{ item.signatures.length }}/{{ item.requiredSigners.length }}
          </v-chip>
          <v-chip v-else-if="item.signatures.length > 0" x-small color="orange" dark>
            {{ item.signatures.length }}/{{ item.requiredSigners.length }}
          </v-chip>
          <v-chip v-else x-small color="grey" dark> 0/{{ item.requiredSigners ? item.requiredSigners.length : 0 }} </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon small @click.stop="downloadPdf(item)">
            <v-icon small>mdi-file-pdf-box</v-icon>
          </v-btn>
          <v-btn icon small @click.stop="openDetailDialog(item)">
            <v-icon small>mdi-eye</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- CREATE / EDIT DIALOG -->
    <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" v-model="createDialog" max-width="800" persistent scrollable>
      <v-card dark>
        <v-card-title>
          <v-icon left>{{ isEditMode ? "mdi-pencil" : "mdi-plus-circle" }}</v-icon>
          {{ isEditMode ? "Personalanliegen bearbeiten" : "Neues Personalanliegen" }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="max-height: 70vh">
          <v-form ref="createForm" v-model="formValid" class="pt-3">
            <h3 class="mb-3">Allgemein</h3>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.studio"
                  :items="studioOptions"
                  item-text="name"
                  item-value="_id"
                  label="Studio *"
                  outlined
                  dense
                  dark
                  :disabled="!canManage && !isEditMode"
                  :hint="!canManage ? 'Dein angemeldetes Studio' : ''"
                  persistent-hint
                  :rules="[(v) => !!v || 'Pflichtfeld']" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model="form.priority" :items="priorityOptions" label="Priorität" outlined dense dark />
              </v-col>

              <v-col cols="12">
                <v-subheader class="pl-0">Betroffener Mitarbeiter *</v-subheader>
                <user-search
                  :value="form.affectedEmployee"
                  ignoreRights
                  instructor
                  :key="'emp-' + form.affectedEmployee"
                  label="Mitarbeiter suchen..."
                  @update="updateAffectedEmployee"></user-search>
                <div v-if="!form.affectedEmployee" class="red--text text--lighten-2 caption mt-1">Pflichtfeld</div>
              </v-col>

              <v-col cols="12">
                <v-combobox
                  v-model="form.category"
                  :items="categoryOptions"
                  :search-input.sync="categorySearch"
                  label="Kategorie *"
                  outlined
                  dense
                  dark
                  hide-no-data
                  :rules="[(v) => !!v || 'Pflichtfeld']"
                  placeholder="z.B. Lohnerhöhung, Stellenprozent-Anpassung..."
                  :loading="loadingCategories">
                  <template v-slot:no-data>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>
                          Neue Kategorie "<strong>{{ categorySearch }}</strong
                          >" wird angelegt
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-combobox>
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="form.title"
                  label="Titel *"
                  placeholder="Kurze Zusammenfassung"
                  outlined
                  dense
                  dark
                  :rules="[(v) => !!v || 'Pflichtfeld']" />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field v-model="form.requestedEffectiveDate" label="Gewünschtes Umsetzungsdatum" type="date" outlined dense dark />
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>
            <h3 class="mb-3">Beschreibung</h3>
            <v-row dense>
              <v-col cols="12">
                <v-textarea
                  v-model="form.description"
                  label="Beschreibung *"
                  outlined
                  dense
                  dark
                  rows="5"
                  placeholder="Beschreibe das Anliegen ausführlich (Hintergrund, aktuelle Situation, gewünschte Änderung)..."
                  :rules="[(v) => !!v || 'Pflichtfeld']" />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.creatorComment"
                  label="Zusätzlicher Kommentar (optional)"
                  outlined
                  dense
                  dark
                  rows="2"
                  placeholder="Anmerkungen, Empfehlung, Begründung..." />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCreateDialog" :disabled="saving">Abbrechen</v-btn>
          <v-btn color="primary" depressed :loading="saving" :disabled="!formValid || !form.affectedEmployee" @click="saveRequest">
            {{ isEditMode ? "Speichern" : "Erfassen & an HR senden" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DETAIL DIALOG -->
    <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" v-model="detailDialog" max-width="900" scrollable>
      <v-card dark v-if="currentRequest">
        <v-card-title>
          <v-icon left>mdi-file-document</v-icon>
          {{ currentRequest.title }}
          <v-spacer></v-spacer>
          <v-chip small :color="priorityColor(currentRequest.priority)" dark class="mr-2">
            {{ priorityLabel(currentRequest.priority) }}
          </v-chip>
          <v-chip small :color="statusColor(currentRequest.status)" dark class="mr-2">
            {{ statusLabel(currentRequest.status) }}
          </v-chip>
          <v-btn icon v-if="canEdit(currentRequest)" @click="openEditDialog(currentRequest)" title="Bearbeiten">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="max-height: 70vh">
          <!-- Visierungs-Banner -->
          <v-alert v-if="canSign && !hasUserSigned(currentRequest)" type="info" text dense class="mt-3">
            <div class="d-flex justify-space-between align-center flex-wrap">
              <span>Dieses Anliegen wartet auf deine Visierung.</span>
              <v-btn small color="success" depressed :loading="signing" @click="signRequest">
                <v-icon left small>mdi-pen</v-icon>Jetzt visieren
              </v-btn>
            </div>
          </v-alert>

          <v-alert v-if="canSign && hasUserSigned(currentRequest)" type="success" text dense class="mt-3">
            <div class="d-flex justify-space-between align-center flex-wrap">
              <span>Du hast dieses Anliegen visiert.</span>
              <v-btn small text color="error" :loading="signing" @click="unsignRequest"> Visierung zurückziehen </v-btn>
            </div>
          </v-alert>

          <v-row class="mt-2">
            <v-col cols="12" md="6">
              <h4 class="mb-2">Allgemein</h4>
              <detail-row label="Studio" :value="currentRequest.studio && currentRequest.studio.name" />
              <detail-row label="Kategorie" :value="currentRequest.category" />
              <detail-row
                label="Mitarbeiter"
                :value="
                  currentRequest.affectedEmployee ? currentRequest.affectedEmployee.vorname + ' ' + currentRequest.affectedEmployee.nachname : '-'
                " />
              <detail-row
                label="Erstellt von"
                :value="currentRequest.createdBy ? currentRequest.createdBy.vorname + ' ' + currentRequest.createdBy.nachname : '-'" />
              <detail-row label="Erstellt am" :value="formatDateTime(currentRequest.createdAt)" />
            </v-col>
            <v-col cols="12" md="6">
              <h4 class="mb-2">Details</h4>
              <detail-row label="Gewünschtes Datum" :value="formatDate(currentRequest.requestedEffectiveDate)" />
              <detail-row label="HR benachrichtigt" :value="currentRequest.hrMailSent ? 'JA' : 'NEIN'" />
              <detail-row
                label="Visierungen"
                :value="
                  (currentRequest.signatures ? currentRequest.signatures.length : 0) +
                  ' / ' +
                  (currentRequest.requiredSigners ? currentRequest.requiredSigners.length : 0)
                " />
            </v-col>
            <v-col cols="12">
              <h4 class="mb-2">Beschreibung</h4>
              <p class="grey--text text--lighten-2" style="white-space: pre-wrap">
                {{ currentRequest.description || "-" }}
              </p>
            </v-col>
            <v-col cols="12" v-if="currentRequest.creatorComment">
              <h4 class="mb-2">Kommentar Ersteller</h4>
              <p class="grey--text text--lighten-2" style="white-space: pre-wrap">
                {{ currentRequest.creatorComment }}
              </p>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Visierungen -->
          <h4 class="mb-2">
            <v-icon left small>mdi-check-decagram</v-icon>
            Visierungen
          </h4>
          <v-list dense color="transparent" dark>
            <v-list-item v-for="signer in currentRequest.requiredSigners" :key="signer._id" class="px-0">
              <v-list-item-avatar size="32" :color="isSigned(currentRequest, signer._id) ? 'green' : 'grey darken-2'">
                <v-icon small dark v-if="isSigned(currentRequest, signer._id)">mdi-check</v-icon>
                <v-icon small dark v-else>mdi-clock-outline</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ signer.vorname }} {{ signer.nachname }}</v-list-item-title>
                <v-list-item-subtitle v-if="isSigned(currentRequest, signer._id)" class="caption">
                  Visiert am {{ getSignatureDate(currentRequest, signer._id) }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-else class="caption">Ausstehend</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="!currentRequest.requiredSigners || currentRequest.requiredSigners.length === 0">
              <v-list-item-title class="grey--text">Keine Visierer hinterlegt.</v-list-item-title>
            </v-list-item>
          </v-list>

          <v-divider class="my-4"></v-divider>

          <!-- Status ändern -->
          <div v-if="canManage" class="mb-4">
            <h4 class="mb-2">Status verwalten</h4>
            <v-btn-toggle v-model="currentRequest.status" mandatory dense @change="changeStatus">
              <v-btn small value="open">Offen</v-btn>
              <v-btn small value="in_progress">In Bearbeitung</v-btn>
              <v-btn small value="done">Erledigt</v-btn>
              <v-btn small value="rejected">Abgelehnt</v-btn>
            </v-btn-toggle>
          </div>

          <!-- Historie -->
          <div v-if="currentRequest.editHistory && currentRequest.editHistory.length > 0">
            <v-divider class="my-4"></v-divider>
            <h4 class="mb-2">
              <v-icon left small>mdi-history</v-icon>
              Bearbeitungshistorie ({{ currentRequest.editHistory.length }})
            </h4>
            <v-expansion-panels dark flat>
              <v-expansion-panel
                v-for="(h, idx) in [...currentRequest.editHistory].reverse()"
                :key="idx"
                style="background: rgba(255, 255, 255, 0.03)">
                <v-expansion-panel-header>
                  <div>
                    <strong>{{ h.user ? h.user.vorname + " " + h.user.nachname : "Unbekannt" }}</strong>
                    <span class="caption grey--text ml-2">{{ formatDateTime(h.editedAt) }}</span>
                    <v-chip x-small class="ml-2" color="blue-grey" dark> {{ h.changes ? h.changes.length : 0 }} Änderungen </v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-for="(c, cidx) in h.changes" :key="cidx" class="mb-2" style="font-size: 13px">
                    <strong>{{ c.field }}:</strong>
                    <span style="color: #c0392b">{{ formatHistoryValue(c.oldValue, c.field) }}</span>
                    →
                    <span style="color: #27ae60">{{ formatHistoryValue(c.newValue, c.field) }}</span>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Kommentare -->
          <h4 class="mb-2">
            <v-icon left small>mdi-comment-multiple</v-icon>
            Kommentare ({{ currentRequest.comments ? currentRequest.comments.length : 0 }})
          </h4>
          <div v-if="currentRequest.comments && currentRequest.comments.length > 0">
            <v-card v-for="(c, idx) in currentRequest.comments" :key="idx" class="mb-2 pa-3" color="rgba(255,255,255,0.05)" flat>
              <div class="d-flex justify-space-between align-center mb-1">
                <strong>{{ c.user ? c.user.vorname + " " + c.user.nachname : "Unbekannt" }}</strong>
                <span class="caption grey--text">{{ formatDateTime(c.createdAt) }}</span>
              </div>
              <div style="white-space: pre-wrap">{{ c.comment }}</div>
            </v-card>
          </div>
          <p v-else class="grey--text">Noch keine Kommentare.</p>

          <v-textarea v-model="newComment" label="Kommentar hinzufügen" outlined dense dark rows="2" class="mt-3" hide-details />
          <div class="text-right mt-2">
            <v-btn small color="primary" :disabled="!newComment.trim()" :loading="savingComment" @click="addComment">
              <v-icon left small>mdi-send</v-icon>Kommentar senden
            </v-btn>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn text color="error" v-if="canManage" @click="deleteRequest"> <v-icon left>mdi-delete</v-icon>Löschen </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="downloadPdf(currentRequest)"> <v-icon left>mdi-file-pdf-box</v-icon>PDF </v-btn>
          <v-btn text @click="detailDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import userSearch from "@/components/userSearch.vue"
import { mapState, mapGetters } from "vuex"

export default {
  name: "PersonalRequests",
  components: {
    userSearch,
    "detail-row": {
      props: ["label", "value"],
      template: `
        <div class="d-flex mb-1" style="font-size: 13px;">
          <span class="grey--text" style="min-width: 140px;">{{ label }}:</span>
          <span>{{ value || '-' }}</span>
        </div>`,
    },
  },
  data() {
    return {
      userService,
      loading: false,
      saving: false,
      savingComment: false,
      signing: false,
      loadingCategories: false,
      requests: [],
      categoryOptions: [],
      categorySearch: "",
      filterStudio: "all",
      filterStatus: "open",
      filterPriority: "all",
      editingId: null,
      createDialog: false,
      detailDialog: false,
      formValid: false,
      currentRequest: null,
      newComment: "",
      form: this.getEmptyForm(),
      tableHeaders: [
        { text: "Erstellt", value: "createdAt" },
        { text: "Titel", value: "title" },
        { text: "Kategorie", value: "category" },
        { text: "Mitarbeiter", value: "affectedEmployee" },
        { text: "Studio", value: "studio" },
        { text: "Priorität", value: "priority" },
        { text: "Status", value: "status" },
        { text: "Visiert", value: "signatures", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      statusFilterOptions: [
        { text: "Alle", value: "all" },
        { text: "Offen", value: "open" },
        { text: "In Bearbeitung", value: "in_progress" },
        { text: "Erledigt", value: "done" },
        { text: "Abgelehnt", value: "rejected" },
      ],
      priorityFilterOptions: [
        { text: "Alle", value: "all" },
        { text: "Hoch", value: "high" },
        { text: "Normal", value: "normal" },
        { text: "Tief", value: "low" },
      ],
      priorityOptions: [
        { text: "Tief", value: "low" },
        { text: "Normal", value: "normal" },
        { text: "Hoch", value: "high" },
      ],
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    ...mapGetters("studioStore", ["getSelectedStudioId"]),
    isEditMode() {
      return !!this.editingId
    },
    canManage() {
      return this.userService.hasRight("d_managePersonalRequests")
    },
    canSign() {
      // User darf visieren, wenn er in der requiredSigners-Liste des aktuellen Requests ist
      if (!this.currentRequest || !this.currentRequest.requiredSigners) return false
      const uid = this.userService.getUserId().toString()
      return this.currentRequest.requiredSigners.some((s) => s._id.toString() === uid)
    },
    studioOptions() {
      return this.storeStudios.filter((s) => s.code && s.code !== "ot" && s.code !== "alibey").map((s) => ({ _id: s._id, name: s.name }))
    },
    studioFilterOptions() {
      return [{ _id: "all", name: "Alle Studios" }, ...this.studioOptions]
    },
  },
  watch: {
    categorySearch(val) {
      if (val && val.length >= 2) {
        this.searchCategories(val)
      }
    },
  },
  methods: {
    getEmptyForm() {
      return {
        studio: this.canManage ? null : this.getSelectedStudioId,
        affectedEmployee: null,
        category: "",
        title: "",
        description: "",
        creatorComment: "",
        requestedEffectiveDate: null,
        priority: "normal",
      }
    },
    async loadRequests() {
      this.loading = true
      try {
        const params = {}
        if (this.canManage && this.filterStudio && this.filterStudio !== "all") params.studio = this.filterStudio
        if (this.filterStatus && this.filterStatus !== "all") params.status = this.filterStatus
        if (this.filterPriority && this.filterPriority !== "all") params.priority = this.filterPriority
        this.requests = await api.personalRequests.fetch(params)
      } catch (e) {
        console.error("Fehler beim Laden:", e)
      } finally {
        this.loading = false
      }
    },
    async loadCategories() {
      this.loadingCategories = true
      try {
        const cats = await api.personalRequests.fetchCategories()
        this.categoryOptions = cats.map((c) => c.name)
      } catch (e) {
        console.error("Fehler beim Laden der Kategorien:", e)
      } finally {
        this.loadingCategories = false
      }
    },
    async searchCategories(query) {
      try {
        const cats = await api.personalRequests.fetchCategories(query)
        const names = cats.map((c) => c.name)
        // Mergen mit bereits geladenen Kategorien
        const merged = [...new Set([...this.categoryOptions, ...names])]
        this.categoryOptions = merged
      } catch (e) {
        // silent
      }
    },
    openCreateDialog() {
      this.form = this.getEmptyForm()
      this.editingId = null
      this.createDialog = true
      this.$nextTick(() => {
        if (this.$refs.createForm) this.$refs.createForm.resetValidation()
      })
    },
    openEditDialog(request) {
      this.form = {
        studio: request.studio && request.studio._id ? request.studio._id : request.studio,
        affectedEmployee: request.affectedEmployee && request.affectedEmployee._id ? request.affectedEmployee._id : request.affectedEmployee,
        category: request.category || "",
        title: request.title || "",
        description: request.description || "",
        creatorComment: request.creatorComment || "",
        requestedEffectiveDate: request.requestedEffectiveDate ? this.$moment(request.requestedEffectiveDate).format("YYYY-MM-DD") : null,
        priority: request.priority || "normal",
      }
      this.editingId = request._id
      this.detailDialog = false
      this.createDialog = true
    },
    closeCreateDialog() {
      this.createDialog = false
      this.editingId = null
    },
    updateAffectedEmployee(value) {
      // userSearch gibt entweder die User-ID oder ein User-Objekt zurück
      if (!value) {
        this.form.affectedEmployee = null
      } else if (typeof value === "object") {
        this.form.affectedEmployee = value._id || value
      } else {
        this.form.affectedEmployee = value
      }
    },
    async saveRequest() {
      if (!this.$refs.createForm.validate()) return
      if (!this.form.affectedEmployee) {
        this.$toast && this.$toast.error("Bitte einen Mitarbeiter auswählen")
        return
      }
      this.saving = true
      try {
        // Kategorie kann vom v-combobox ein Objekt oder String sein
        const payload = { ...this.form }
        if (typeof payload.category === "object" && payload.category !== null) {
          payload.category = payload.category.text || payload.category.name || ""
        }

        if (this.isEditMode) {
          const updated = await api.personalRequests.update(this.editingId, payload)
          this.currentRequest = updated
        } else {
          await api.personalRequests.create(payload)
        }
        this.createDialog = false
        this.editingId = null
        this.loadRequests()
        this.loadCategories()
      } catch (e) {
        console.error("Fehler beim Speichern:", e)
        const msg = e.response && e.response.data && e.response.data.error ? e.response.data.error : "Fehler beim Speichern"
        alert(msg)
      } finally {
        this.saving = false
      }
    },
    async openDetailDialog(request) {
      try {
        this.currentRequest = await api.personalRequests.fetchOne(request._id)
        this.newComment = ""
        this.detailDialog = true
      } catch (e) {
        console.error("Fehler beim Laden des Details:", e)
      }
    },
    async changeStatus(status) {
      if (!this.currentRequest) return
      try {
        const updated = await api.personalRequests.updateStatus(this.currentRequest._id, status)
        this.currentRequest = updated
        this.loadRequests()
      } catch (e) {
        console.error("Fehler beim Statuswechsel:", e)
      }
    },
    async signRequest() {
      if (!this.currentRequest) return
      this.signing = true
      try {
        const updated = await api.personalRequests.sign(this.currentRequest._id)
        this.currentRequest = updated
        this.loadRequests()
      } catch (e) {
        console.error("Fehler beim Visieren:", e)
        alert(e.response && e.response.data && e.response.data.error ? e.response.data.error : "Fehler beim Visieren")
      } finally {
        this.signing = false
      }
    },
    async unsignRequest() {
      if (!this.currentRequest) return
      if (!confirm("Visierung wirklich zurückziehen?")) return
      this.signing = true
      try {
        const updated = await api.personalRequests.unsign(this.currentRequest._id)
        this.currentRequest = updated
        this.loadRequests()
      } catch (e) {
        console.error("Fehler beim Zurückziehen:", e)
      } finally {
        this.signing = false
      }
    },
    async addComment() {
      if (!this.newComment.trim() || !this.currentRequest) return
      this.savingComment = true
      try {
        const updated = await api.personalRequests.addComment(this.currentRequest._id, this.newComment)
        this.currentRequest = updated
        this.newComment = ""
        this.loadRequests()
      } catch (e) {
        console.error("Fehler beim Kommentar:", e)
      } finally {
        this.savingComment = false
      }
    },
    async deleteRequest() {
      if (!this.currentRequest) return
      if (!confirm("Personalanliegen wirklich löschen?")) return
      try {
        await api.personalRequests.delete(this.currentRequest._id)
        this.detailDialog = false
        this.loadRequests()
      } catch (e) {
        console.error("Fehler beim Löschen:", e)
      }
    },
    downloadPdf(request) {
      const employeeName = request.affectedEmployee ? `${request.affectedEmployee.vorname}_${request.affectedEmployee.nachname}` : "Mitarbeiter"
      const fileName = `Personalanliegen_${employeeName}_${this.$moment(request.createdAt).format("YYYY-MM-DD")}.pdf`
      api.personalRequests.downloadPdf(request._id, fileName)
    },
    canEdit(request) {
      if (!request || request.status === "done") return false
      if (this.canManage) return true
      const creatorId = request.createdBy && request.createdBy._id ? request.createdBy._id : request.createdBy
      return creatorId && creatorId.toString() === this.userService.getUserId().toString()
    },
    hasUserSigned(request) {
      if (!request || !request.signatures) return false
      const uid = this.userService.getUserId().toString()
      return request.signatures.some((s) => {
        const sid = s.user && s.user._id ? s.user._id.toString() : s.user.toString()
        return sid === uid
      })
    },
    isSigned(request, userId) {
      if (!request || !request.signatures) return false
      return request.signatures.some((s) => {
        const sid = s.user && s.user._id ? s.user._id.toString() : s.user.toString()
        return sid === userId.toString()
      })
    },
    getSignatureDate(request, userId) {
      if (!request || !request.signatures) return ""
      const sig = request.signatures.find((s) => {
        const sid = s.user && s.user._id ? s.user._id.toString() : s.user.toString()
        return sid === userId.toString()
      })
      return sig ? this.$moment(sig.signedAt).format("DD.MM.YYYY HH:mm") : ""
    },
    formatDate(d) {
      return d ? this.$moment(d).format("DD.MM.YYYY") : "-"
    },
    formatDateTime(d) {
      return d ? this.$moment(d).format("DD.MM.YYYY HH:mm") : "-"
    },
    formatHistoryValue(val, fieldLabel) {
      if (val === null || val === undefined || val === "") return "-"
      if (typeof val === "boolean") return val ? "JA" : "NEIN"
      if (typeof val === "object" && val !== null) {
        if (val.vorname) return `${val.vorname} ${val.nachname || ""}`.trim()
        if (val.name) return val.name
        if (val._id) return val._id
        return JSON.stringify(val)
      }
      if (typeof val === "string" && val.match(/^\d{4}-\d{2}-\d{2}/)) {
        const m = this.$moment(val)
        if (m.isValid()) {
          return fieldLabel === "Gewünschtes Datum" ? m.format("DD.MM.YYYY") : m.format("DD.MM.YYYY")
        }
      }
      return String(val)
    },
    statusColor(s) {
      if (s === "done") return "green"
      if (s === "in_progress") return "orange"
      if (s === "rejected") return "red darken-2"
      return "red"
    },
    statusLabel(s) {
      if (s === "done") return "Erledigt"
      if (s === "in_progress") return "In Bearbeitung"
      if (s === "rejected") return "Abgelehnt"
      return "Offen"
    },
    priorityColor(p) {
      if (p === "high") return "red"
      if (p === "low") return "grey"
      return "blue"
    },
    priorityLabel(p) {
      if (p === "high") return "Hoch"
      if (p === "low") return "Tief"
      return "Normal"
    },
  },
  created() {
    this.loadRequests()
    this.loadCategories()
  },
}
</script>

<style scoped>
.transparent {
  background: transparent !important;
}
</style>
